import { useState } from 'react';
import { usePurchaseOrder } from '../../../hooks/usePurchaseOrders';
import { apiErrorStatus } from '../../../utils/apiError';
import { useMyPermissions } from '../../../hooks/useMyPermissions';
import { Drawer, DrawerSection, KVRow } from '../Drawer';
import { EventTimeline } from '../EventTimeline';
import { PatientLink } from '../PatientLink';
import { poAttentionFlags } from './poStatus';
import { toTimelineEvents } from './poEvents';
import { PoAttentionNotices } from './PoAttentionNotices';
import {
    PurchaseOrderActionModalHost,
    PurchaseOrderActionsSection,
    type PurchaseOrderActionId
} from './PurchaseOrderActions';
import { PurchaseOrderBillingZone } from './PurchaseOrderBillingZone';
import { PurchaseOrderDispatchZone } from './PurchaseOrderDispatchZone';
import { PurchaseOrderShippingZone } from './PurchaseOrderShippingZone';

function shortRef(id: string): string {
    return id.slice(0, 8).toUpperCase();
}

interface PurchaseOrderDrawerProps {
    purchaseOrderId: string;
    onClose: () => void;
    /** Swaps this drawer for the charge drawer on the hosting page. */
    onOpenCharge: (chargeId: string) => void;
    onBack?: () => void;
}

export function PurchaseOrderDrawer({
    purchaseOrderId,
    onClose,
    onOpenCharge,
    onBack
}: PurchaseOrderDrawerProps) {
    const { purchaseOrder, isLoading, isError, error } = usePurchaseOrder(purchaseOrderId);
    const { hasPermission } = useMyPermissions();
    const canEdit = hasPermission('billing.update');
    const [openAction, setOpenAction] = useState<PurchaseOrderActionId | null>(null);

    if (isLoading) {
        return (
            <Drawer title="Purchase order" onClose={onClose} onBack={onBack}>
                <div className="flex items-center justify-center gap-2 text-gray-400 py-16">
                    <div className="w-4 h-4 rounded-full border-2 border-gray-300 border-t-gray-600 animate-spin" />
                    <span className="text-sm">Loading purchase order…</span>
                </div>
            </Drawer>
        );
    }

    if (isError || !purchaseOrder) {
        return (
            <Drawer title="Purchase order" onClose={onClose} onBack={onBack}>
                <p className="text-sm text-gray-500 py-16 text-center">
                    {apiErrorStatus(error) === 404
                        ? 'Purchase order not found.'
                        : 'Could not load this purchase order. Try again.'}
                </p>
            </Drawer>
        );
    }

    return (
        <>
            <Drawer
                title={purchaseOrder.poNumber}
                subtitle={`${purchaseOrder.vendor.name} · ${purchaseOrder.practiceName}`}
                onClose={onClose}
                onBack={onBack}
            >
                <PoAttentionNotices flags={poAttentionFlags(purchaseOrder)} />

                <DrawerSection title="Details">
                    <div className="flex flex-col">
                        <KVRow k="Vendor">{purchaseOrder.vendor.name}</KVRow>
                        <KVRow k="Practice">{purchaseOrder.practiceName}</KVRow>
                        <KVRow k="Patient">
                            <PatientLink
                                patientId={purchaseOrder.patientId}
                                patientName={purchaseOrder.patientName}
                            >
                                {purchaseOrder.patientName ?? (
                                    <span className="text-gray-400">-</span>
                                )}
                            </PatientLink>
                        </KVRow>
                        {/* Provenance only - cost belongs to the lines. */}
                        <KVRow k="Charge">
                            <button
                                type="button"
                                onClick={() => onOpenCharge(purchaseOrder.originatingChargeId)}
                                className="font-mono text-[12px] underline decoration-gray-300 underline-offset-2 hover:text-gray-900"
                            >
                                #{shortRef(purchaseOrder.originatingChargeId)}
                            </button>
                        </KVRow>
                    </div>
                </DrawerSection>

                <PurchaseOrderDispatchZone purchaseOrder={purchaseOrder} />
                <PurchaseOrderShippingZone purchaseOrder={purchaseOrder} />
                <PurchaseOrderBillingZone purchaseOrder={purchaseOrder} />

                <DrawerSection title="Activity">
                    <EventTimeline events={toTimelineEvents(purchaseOrder.events)} />
                </DrawerSection>

                {canEdit && <PurchaseOrderActionsSection onSelect={setOpenAction} />}

                {!canEdit && (
                    <p className="text-xs text-gray-400 pb-2">
                        Read-only: you don't have billing edit access.
                    </p>
                )}
            </Drawer>

            {openAction && (
                <PurchaseOrderActionModalHost
                    actionId={openAction}
                    purchaseOrder={purchaseOrder}
                    onClose={() => setOpenAction(null)}
                />
            )}
        </>
    );
}
